import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getLenis, scrollToTop } from './scroll'
import { refreshMotion, prefersReducedMotion } from './index'
import { syncHeaderNav } from './nav-sync'

const MAIN_SELECTOR = '#main'
const DURATION_OUT = 0.45
const DURATION_IN = 0.7

let isNavigating = false

const isTransitionLink = (link) => {
	if (!link || 'A' !== link.tagName) {
		return false
	}

	const href = link.getAttribute('href')

	if ( ! href || '#' === href || href.startsWith( '#' ) || href.startsWith( 'mailto:' ) || href.startsWith( 'tel:' ) ) {
		return false
	}

	if ( link.target === '_blank' || link.hasAttribute( 'download' ) ) {
		return false
	}

	if ( link.closest( '[data-no-transition]' ) || link.closest( '.site-search__form' ) ) {
		return false
	}

	let url

	try {
		url = new URL( link.href, window.location.href )
	} catch {
		return false
	}

	if ( url.origin !== window.location.origin ) {
		return false
	}

	if ( url.pathname.includes( '/wp-admin' ) || url.pathname.includes( '/wp-login' ) ) {
		return false
	}

	return url.href !== window.location.href
}

const animateOut = () =>
	gsap.to( MAIN_SELECTOR, {
		autoAlpha: 0,
		y: -18,
		duration: DURATION_OUT,
		ease: 'power2.in',
	} )

const animateIn = () =>
	gsap.fromTo(
		MAIN_SELECTOR,
		{ autoAlpha: 0, y: 24 },
		{
			autoAlpha: 1,
			y: 0,
			duration: DURATION_IN,
			ease: 'power3.out',
		}
	)

const updateDocument = (doc) => {
	const newMain = doc.querySelector( MAIN_SELECTOR )
	const main = document.querySelector( MAIN_SELECTOR )

	if ( ! newMain || ! main ) {
		return false
	}

	main.innerHTML = newMain.innerHTML

	const title = doc.querySelector( 'title' )

	if ( title ) {
		document.title = title.textContent
	}

	if ( doc.body?.className ) {
		document.body.className = doc.body.className
	}

	syncHeaderNav( doc )

	return true
}

const navigate = async (url, { push = true } = {}) => {
	if ( prefersReducedMotion() ) {
		window.location.assign( url )
		return
	}

	if ( isNavigating ) {
		return
	}

	isNavigating = true
	document.documentElement.classList.add( 'is-page-transitioning' )

	const lenis = getLenis()
	lenis?.stop()

	await animateOut()

	scrollToTop()

	try {
		const response = await fetch( url, {
			credentials: 'same-origin',
			headers: { 'X-Requested-With': 'JuccaPageTransition' },
		} )

		if ( ! response.ok ) {
			throw new Error( 'Navigation failed' )
		}

		const html = await response.text()
		const doc = new DOMParser().parseFromString( html, 'text/html' )

		if ( ! updateDocument( doc ) ) {
			window.location.assign( url )
			return
		}

		if ( push ) {
			history.pushState( { juccaPage: true }, '', url )
		}

		scrollToTop()

		ScrollTrigger.getAll().forEach( (trigger) => trigger.kill() )
		refreshMotion()

		await animateIn()

		scrollToTop()

		lenis?.start()
		ScrollTrigger.clearScrollMemory()
		ScrollTrigger.refresh()

		document.querySelector( MAIN_SELECTOR )?.focus( { preventScroll: true } )
	} catch {
		window.location.assign( url )
	} finally {
		isNavigating = false
		document.documentElement.classList.remove( 'is-page-transitioning' )
	}
}

export const initPageTransitions = () => {
	if ( prefersReducedMotion() ) {
		return
	}

	history.replaceState( { juccaPage: true }, '', window.location.href )

	document.addEventListener( 'click', (event) => {
		if ( event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || 0 !== event.button ) {
			return
		}

		const link = event.target.closest( 'a' )

		if ( ! isTransitionLink( link ) ) {
			return
		}

		event.preventDefault()
		navigate( link.href )
	} )

	window.addEventListener( 'popstate', () => {
		navigate( window.location.href, { push: false } )
	} )
}
